<template>
    <div class="damage-list">
        <div v-if="localDamages?.length" class="damage-list__container">
            <div
                v-for="(damage, index) in localDamages"
                :key="damage.id"
                class="damage-item"
            >
                <div class="damage-item__header-wrapper">
                    <div class="damage-item__header">
                        Повреждение {{ index + 1 }}
                    </div>

                    <div class="damage-item__delete" @click="removeDamage(damage.id)">
                        <img v-svg-inline src="@/assets/icons/ui/close.svg" />
                    </div>
                </div>

                <div class="damage-item__content">
                    <div class="damage-item__details">
                        <div class="damage-item__row">
                            <UISelect
                                v-model="damage.selectedElement"
                                :options="elementOptions"
                                label="Элемент*"
                                :error-message="index < errors.length ? errors[index]?.symbolic_code?.join() ?? '' : ''"
                            />

                            <UISelect
                                v-model="damage.selectedDamageType"
                                :options="damageTypeOptions"
                                label="Тип повреждения*"
                                :error-message="index < errors.length ? errors[index]?.type?.join() ?? '' : ''"
                            />

                            <UIInput
                                v-model="damage.coatingThickness"
                                type="text"
                                label="Толщина ЛКП"
                                :error-message="index < errors.length ? errors[index]?.paintwork_thickness?.join() ?? '' : ''"
                                only-numbers
                            />
                        </div>

                        <div class="damage-item__row full-width">
                            <UITextArea
                                v-model="damage.userComment"
                                label="Комментарий"
                                :height="100"
                                :max-length="null"
                                :resizable="false"
                                :error-message="index < errors.length ? errors[index]?.comment?.join() ?? '' : ''"
                            />
                        </div>
                    </div>

                    <div class="damage-item__media">
                        <div class="damage-item__media-header">Фотография повреждения</div>

                        <div class="damage-item__media-container" @click="triggerFileInput(index)">
                            <div
                                v-if="!damage.previewImage"
                                class="damage-item__media-action"
                            >
                                <img v-svg-inline src="@/assets/icons/ui/camera.svg" />
                                <div class="damage-item__media-label">Выбрать Фотографию</div>
                            </div>
                            <img
                                v-else
                                :src="damage.previewImage"
                                class="damage-item__media-preview"
                            />

                            <input
                                :ref="el => setFileInputRef(el as HTMLInputElement | null, index)"
                                type="file"
                                class="hidden-input"
                                accept="image/jpg, image/jpeg, image/png, image/webp"
                                @change="handleFileChange($event, index)"
                            />
                        </div>

                        <span v-if="index < errors.length && errors[index]?.photo" class="form-field__error">
                            {{ index < errors.length ? errors[index].photo?.join() ?? '' : '' }}
                        </span>
                    </div>
                </div>
            </div>
        </div>

        <div
            class="damage-list__actions"
            @click="addDamage"
        >
            <div class="damage-list__add-button">
                <img v-svg-inline src="@/assets/icons/ui/plus.svg" />

                <span>Добавить еще повреждение</span>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
    import { ref, onMounted, watch } from 'vue';

    import { DamageItem } from '@/types/form.type.ts';
    import {
        CarDamageCreateFormErrors,
        getCarDamageElementSelectOptions,
        getCarDamageTypeSelectOptions,
    } from '@/types/car.type.ts';

    import UIInput from '@/components/ui/UIInput/UIInput.vue';
    import UISelect from '@/components/ui/UISelect/UISelect.vue';
    import UITextArea from '@/components/ui/UITextArea/UITextArea.vue';

    const props = defineProps<{
        initialDamages?: DamageItem[],
        errors: (CarDamageCreateFormErrors | undefined)[],
    }>();

    const emit = defineEmits<{
        (e: 'damage-updated', updatedDamages: DamageItem[]): void,
    }>();

    const localDamages = ref<DamageItem[]>([]);
    const fileInputRefs = ref<(HTMLInputElement | null)[]>([]);

    const elementOptions = ref(getCarDamageElementSelectOptions());
    const damageTypeOptions = ref(getCarDamageTypeSelectOptions());

    watch(
        () => localDamages.value,
        () => emitDamageUpdated(),
        { deep: true },
    );

    watch(
        () => props.initialDamages,
        () => fillInitialMedia(),
        { deep: true },
    );

    function addDamage(): void {
        localDamages.value.push({
            id: Date.now(),
            selectedElement: undefined,
            selectedDamageType: undefined,
            coatingThickness: '',
            userComment: '',
            previewImage: null,
            file: null,
            apiId: undefined,
        });
    }

    function removeDamage(damageId: number): void {
        localDamages.value = localDamages.value.filter(d => d.id !== damageId);
    }

    function setFileInputRef(el: HTMLInputElement | null, index: number): void {
        if (!el) {
            return;
        }

        fileInputRefs.value[index] = el;
    }

    function triggerFileInput(index: number): void {
        if (fileInputRefs.value[index]) {
            fileInputRefs.value[index]!.click();
        }
    }

    function handleFileChange(e: Event, index: number): void {
        const input = e.target as HTMLInputElement;
        if (!input.files?.length) {
            return;
        }

        const file = input.files[0];
        const reader = new FileReader();
        reader.onload = (ev): void => {
            if (!ev.target?.result) {
                return;
            }

            localDamages.value[index].previewImage = ev.target.result as string;
            localDamages.value[index].file = file;
        };
        reader.readAsDataURL(file);
    }

    function emitDamageUpdated(): void {
        emit('damage-updated', localDamages.value);
    }

    function fillInitialMedia(): void {
        if (props.initialDamages) {
            localDamages.value = props.initialDamages.map(d => ({
                ...d,
                file: d.file,
            }));
        }
    }

    onMounted(() => {
        fillInitialMedia();
    });
</script>

<style scoped lang="scss">
    @use './UIDamageList.scss' as *;
</style>
